import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ServiceDetailPageContainer } from './ServiceDetailPage';
import { FaPaperPlane } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const ServiceImage = styled.img`
  max-width: 200px; /* Smaller size */
  height: auto;
  border-radius: 0; /* Remove border-radius */
  margin: 0.5rem 0 1rem 2rem; /* Adjusted margin for text wrapping */
  box-shadow: none; /* Remove box-shadow */
  float: right; /* Allow text to wrap around */

  @media (max-width: 768px) {
    float: none;
    margin: 1rem auto;
    max-width: 80%;
    display: block;
  }
`;

const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;
  margin-top: 8rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 100%;
`;

export const ContactTitle = styled.h2`
  color: #fff !important;
  margin-top: 0 !important;
`;

const ContactForm = styled.form`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const ContactInput = styled.input`
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const SubmitButton = styled.button`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

function SEO() {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow numbers and limit to exactly 11 digits
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setFormData({
          ...formData,
          [name]: numbersOnly
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: formData.name,
        phone: formData.phone,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_w676du8',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setFormData({ name: '', phone: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceDetailPageContainer>
      <h1>Performans Odaklı SEO</h1>
      <p>Arama motorlarında üst sıralara çıkarak organik trafiğinizi ve marka bilinirliğinizi artırıyoruz. Kapsamlı anahtar kelime araştırması, teknik SEO optimizasyonu ve etkili içerik stratejileri ile dijital görünürlüğünüzü en üst düzeye çıkarıyoruz.</p>

      <ServiceImage src="/media/demirer-ajans-7.png" alt="SEO Görseli 1" />

      <h2>Neden Performans Odaklı SEO Önemlidir?</h2>
      <p>Günümüzde tüketicilerin büyük çoğunluğu ürün ve hizmet arayışlarını arama motorları üzerinden gerçekleştirmektedir. Arama motorlarında üst sıralarda yer almak, potansiyel müşterilere ulaşmanın ve rakiplerinizin önüne geçmenin en maliyet etkin yollarından biridir. Performans odaklı SEO, sadece sıralama değil, aynı zamanda dönüşüm ve gelir artışı hedefler.</p>

      <ServiceImage src="/media/demirer-ajans-8.png" alt="SEO Görseli 2" />

      <h3>Hizmetlerimiz Neleri Kapsar?</h3>
      <ul>
        <li><b>Anahtar Kelime Araştırması:</b> Hedef kitlenizin arama yaptığı anahtar kelimeleri belirleme ve stratejinize entegre etme.</li>
        <li><b>Teknik SEO Optimizasyonu:</b> Web sitenizin teknik altyapısını arama motorları için optimize etme (site hızı, mobil uyumluluk, site haritası, robot.txt vb.).</li>
        <li><b>İçerik Stratejisi ve Optimizasyonu:</b> Anahtar kelimelerle uyumlu, değerli ve kullanıcıyı bilgilendiren içerikler oluşturma ve mevcut içerikleri optimize etme.</li>
        <li><b>Off-Page SEO (Dış SEO):</b> Backlink oluşturma ve marka otoritesini artırma çalışmaları.</li>
        <li><b>Yerel SEO:</b> Yerel işletmeler için coğrafi aramalarda görünürlüğü artırma stratejileri.</li>
        <li><b>Rakip Analizi:</b> Rakiplerinizin SEO stratejilerini analiz ederek kendi avantajlarınızı belirleme.</li>
        <li><b>Raporlama ve İzleme:</b> SEO performansını düzenli olarak izleme, detaylı raporlama ve stratejileri sürekli iyileştirme.</li>
      </ul>

      <h2>Markanız İçin Nasıl Bir SEO Stratejisi Oluşturuyoruz?</h2>
      <ServiceImage src="/media/demirer-ajans-4.png" alt="SEO Görseli 3" />
      <p>Sektörünüzü, hedef kitlenizi ve iş hedeflerinizi detaylı bir şekilde analiz ederek size özel, sürdürülebilir ve performans odaklı bir SEO stratejisi geliştiriyoruz. Amacımız, sadece arama motoru sıralamalarınızı yükseltmek değil, aynı zamanda nitelikli trafik çekerek işinizin büyümesini sağlamaktır.</p>
      <p>Dijital dünyada görünürlüğünüzü artırmak ve organik olarak daha fazla müşteriye ulaşmak için bize güvenebilirsiniz.</p>

      <h2>SEO Hizmetlerimiz</h2>
      <p>SEO hizmetlerimiz, işletmenizin arama motorlarında üst sıralarda yer almasını sağlamak için tasarlanmıştır. Uzman ekibimiz, teknik SEO, içerik optimizasyonu ve bağlantı kurma stratejileri konularında profesyonel çözümler sunar.</p>
      <p>Veri odaklı yaklaşım ve sürekli optimizasyonla, web sitenizin organik trafiğini artırıyor ve arama motoru sıralamalarında yükselmesini sağlıyoruz. İşletmenizin online görünürlüğünü artırmak için bize güvenebilirsiniz.</p>

      <ContactSection>
        <ContactContent>
          <ContactTitle>Sizi Arayalım</ContactTitle>
          <ContactForm onSubmit={handleSubmit}>
            <ContactInput 
              type="text" 
              name="name"
              placeholder="Adınız"
              value={formData.name}
              onChange={handleInputChange}
              required 
            />
            <ContactInput 
              type="tel" 
              name="phone"
              placeholder="Telefon Numaranız (11 Haneli)"
              value={formData.phone}
              onChange={handleInputChange}
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              minLength="11"
              required 
            />
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </ServiceDetailPageContainer>
  );
}

export default SEO; 